import type { CSSProperties } from "react";
import type { AvailableTransition, WorkflowDefinition } from "../../../lib/types";

/**
 * Sprint 12 — horizontal workflow stepper. Renders one pill per state in
 * workflow order with the current state highlighted (active=blue, terminal=gray)
 * and connectors between. States reachable from the current one (per the
 * server-supplied `_transitions`) are clickable; clicking fires `onTransition`,
 * which updates the workflow field. The server enforces guards — a blocked
 * transition surfaces its message as a toast (handled by the caller).
 *
 * Renders nothing when the entity type has no workflow.
 */

interface StageStepperProps {
  workflow: WorkflowDefinition;
  currentState: string;
  transitions: AvailableTransition[];
  onTransition: (newState: string) => void;
  disabled?: boolean;
}

export function StageStepper({ workflow, currentState, transitions, onTransition, disabled }: StageStepperProps) {
  const reachable = new Set(transitions.map((t) => t.to));
  const currentStateLabel =
    workflow.states.find((s) => s.slug === currentState)?.label ?? currentState;

  return (
    <div style={containerStyle} role="group" aria-label="Workflow stage">
      {workflow.states.map((state, idx) => {
        const isCurrent = state.slug === currentState;
        const isReachable = reachable.has(state.slug);
        const isTerminal = state.category === "terminal";
        const clickable = !disabled && isReachable && !isCurrent;
        // Sprint 12b — a state with no declared transition from here reads
        // differently from a reachable next-state: faded + not-allowed + a
        // tooltip explaining why it can't be clicked.
        const nonReachable = !isCurrent && !isReachable;

        const title = isCurrent
          ? `Current: ${state.label}`
          : clickable
            ? `Move to ${state.label}`
            : nonReachable
              ? `No direct transition from ${currentStateLabel}`
              : state.label;

        return (
          <div key={state.slug} style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            {idx > 0 && <span style={connectorStyle} aria-hidden="true" />}
            <button
              onClick={clickable ? () => onTransition(state.slug) : undefined}
              disabled={!clickable}
              aria-current={isCurrent ? "step" : undefined}
              title={title}
              style={pillStyle(isCurrent, clickable, isTerminal, nonReachable)}
            >
              {isCurrent && (
                <i className="ti ti-circle-dot" style={{ fontSize: 8 }} aria-hidden="true" />
              )}
              {state.label}
            </button>
          </div>
        );
      })}
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 2,
  rowGap: 6,
};

const connectorStyle: CSSProperties = {
  width: 12,
  height: 1,
  background: "var(--color-border-secondary)",
  flexShrink: 0,
  margin: "0 1px",
};

function pillStyle(
  isCurrent: boolean,
  clickable: boolean,
  isTerminal: boolean,
  nonReachable: boolean,
): CSSProperties {
  let bg = "var(--color-background-secondary)";
  let fg = "var(--color-text-tertiary)";
  let border = "0.5px solid transparent";

  if (isCurrent) {
    if (isTerminal) {
      bg = "var(--c-gray-600)";
      fg = "#fff";
    } else {
      bg = "var(--c-blue-600)";
      fg = "#fff";
    }
  } else if (clickable) {
    bg = "transparent";
    fg = "var(--color-text-secondary)";
    border = "0.5px solid var(--color-border-secondary)";
  }

  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: bg,
    color: fg,
    border,
    padding: "3px 10px",
    borderRadius: "var(--kuvio-pill-radius)",
    fontSize: 11,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    lineHeight: 1.5,
    // Explicit opacity/cursor override the global button[disabled] rule so the
    // current + reachable pills stay crisp and only no-transition states fade.
    opacity: nonReachable ? 0.45 : 1,
    cursor: clickable ? "pointer" : nonReachable ? "not-allowed" : "default",
    whiteSpace: "nowrap",
  };
}
